<template>
  <section class="label-filter">
    <p
      v-if="!entries.length"
      class="label-filter__empty"
    >
      No labels available.
    </p>
    <ul
      v-else
      class="label-filter__list"
    >
      <li
        v-for="entry in entries"
        :key="entry.label"
        class="label-filter__item"
      >
        <label class="label-filter__option">
          <input
            type="checkbox"
            class="label-filter__checkbox"
            :checked="entry.checked"
            @change="onToggle(entry.label, $event)"
          >
          <span class="label-filter__text">
            <span
              class="label-filter__legend"
              :style="{ backgroundColor: entry.color }"
              aria-hidden="true"
            />
            {{ entry.label }}
            <span class="label-filter__count">({{ entry.count }})</span>
          </span>
        </label>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type {
  AnnotationSchemaPayload,
  AnnotationLabelFilter,
} from '@/components/RegionBasedAnnotation/types/annotation';
import { resolveLabelPalette } from '@/components/RegionBasedAnnotation/utils/slideLabelColors';

const props = defineProps<{
  images?: AnnotationSchemaPayload | null,
  labelFilter?: AnnotationLabelFilter | null,
}>();

const emit = defineEmits<{
  (e: 'toggle', payload: { label: string; checked: boolean }): void
}>();

const images = computed(() => props.images ?? {});
const labelFilter = computed(() => props.labelFilter ?? {});

const entries = computed(() => {
  const counts = new Map<string, number>();
  Object.values(images.value).forEach((image) => {
    image.annotations.forEach((annotation) => {
      const label = annotation.label;
      if (!label) return;
      counts.set(label, (counts.get(label) ?? 0) + 1);
    });
  });
  return Array.from(counts.entries())
    .map(([label, count]) => ({
      label,
      count,
      color: resolveLabelPalette(label, '#9ca3af', '#9ca3af').stroke,
      checked: labelFilter.value[label] !== false,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
});

function onToggle(label: string, event: Event) {
  const target = event.target as HTMLInputElement | null;
  if (!target) return;
  emit('toggle', { label, checked: target.checked });
}
</script>

<style scoped>
.label-filter {
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background-color: #ffffff;
}

.label-filter__empty {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.label-filter__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label-filter__item {
  margin: 0;
}

.label-filter__option {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 16px;
  color: #111827;
}

.label-filter__checkbox {
  width: 16px;
  height: 16px;
}

.label-filter__text {
  display: inline-flex;
  gap: 4px;
  align-items: baseline;
}

.label-filter__legend {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 10px;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.1);
  align-self: center;
}

.label-filter__count {
  color: #6b7280;
  font-size: 14px;
}
</style>
